/**
 * 登录页缓存管理模块
 * 功能：
 * 1. 管理登录页静态资源缓存
 * 2. 缓存登录页DOM和样式信息
 * 3. 确保不缓存用户敏感信息（username, password, token等）
 * 4. 在退出登录时清除所有缓存
 */

const CACHE_KEY = {
  LOGIN_RESOURCES: 'login_page_resources',
  LOGIN_CONFIG: 'login_page_config',
  CACHE_TIMESTAMP: 'login_cache_timestamp'
};

// 缓存过期时间：30天（单位：毫秒）
const CACHE_DURATION = 30 * 24 * 60 * 60 * 1000;

// 定义登录页允许缓存的字段（仅静态资源和配置）
const CACHEABLE_FIELDS = {
  pageHeight: true,
  year: true,
  registerRules: true
};

// 被禁止缓存的敏感字段
const SENSITIVE_FIELDS = [
  'username',
  'password',
  'phone',
  'token',
  'verificationCode',
  'phoneNumber',
  'email'
];

/**
 * 初始化登录页缓存
 * 在登录页mounted时调用
 */
export function initLoginCache() {
  const cached = getLoginCache();
  
  if (cached && isValidCache()) {
    console.log('[LoginCache] 使用缓存的登录页配置');
    return cached;
  } else {
    console.log('[LoginCache] 缓存已失效或不存在，将创建新缓存');
    return null;
  }
}

/**
 * 保存登录页状态到缓存（仅保存允许缓存的字段）
 * @param {Object} pageData - 登录页的data对象
 */
export function cacheLoginPageState(pageData) {
  try {
    const cacheData = {};
    
    // 仅缓存允许的字段
    Object.keys(pageData).forEach(key => {
      if (CACHEABLE_FIELDS[key] && !SENSITIVE_FIELDS.includes(key)) {
        cacheData[key] = pageData[key];
      }
    });
    
    // 保存缓存数据
    sessionStorage.setItem(
      CACHE_KEY.LOGIN_RESOURCES,
      JSON.stringify(cacheData)
    );
    
    // 保存缓存时间戳
    sessionStorage.setItem(
      CACHE_KEY.CACHE_TIMESTAMP,
      String(Date.now())
    );
    
    console.log('[LoginCache] 登录页缓存已保存', cacheData);
  } catch (error) {
    console.error('[LoginCache] 保存缓存失败:', error);
  }
}

/**
 * 获取登录页缓存
 * @returns {Object|null} - 缓存的数据或null
 */
export function getLoginCache() {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY.LOGIN_RESOURCES);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.error('[LoginCache] 读取缓存失败:', error);
    clearLoginCache();
    return null;
  }
}

/**
 * 验证缓存是否有效
 * @returns {boolean} - 缓存是否有效
 */
export function isValidCache() {
  try {
    const timestamp = sessionStorage.getItem(CACHE_KEY.CACHE_TIMESTAMP);
    if (!timestamp) return false;
    
    const cacheTime = parseInt(timestamp, 10);
    const now = Date.now();
    
    // 检查缓存是否过期
    if (now - cacheTime > CACHE_DURATION) {
      console.log('[LoginCache] 缓存已过期');
      clearLoginCache();
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('[LoginCache] 验证缓存失败:', error);
    return false;
  }
}

/**
 * 清除登录页缓存
 * 在退出登录时调用，确保下次登录加载新数据
 */
export function clearLoginCache() {
  try {
    sessionStorage.removeItem(CACHE_KEY.LOGIN_RESOURCES);
    sessionStorage.removeItem(CACHE_KEY.CACHE_TIMESTAMP);
    sessionStorage.removeItem(CACHE_KEY.LOGIN_CONFIG);
    console.log('[LoginCache] 登录页缓存已清除');
  } catch (error) {
    console.error('[LoginCache] 清除缓存失败:', error);
  }
}

/**
 * 清除所有敏感数据
 * 防止任何敏感信息被缓存
 */
export function clearSensitiveData() {
  try {
    // 清除localStorage中的敏感信息
    SENSITIVE_FIELDS.forEach(field => {
      localStorage.removeItem(field);
      sessionStorage.removeItem(field);
    });
    
    // 特别清除token等认证信息
    localStorage.removeItem('token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('access_token');
    
    console.log('[LoginCache] 敏感数据已清除');
  } catch (error) {
    console.error('[LoginCache] 清除敏感数据失败:', error);
  }
}

/**
 * 预加载登录页静态资源
 * 在应用初始化时调用，可加快登录页加载速度
 */
export function preloadLoginResources() {
  try {
    // 注意：登录背景图扇已经在 login.vue 的 CSS 中引用
    // 不需要在此下programmatically打开
    console.log('[LoginCache] 正在预加载登录页静态资源');
  } catch (error) {
    console.error('[LoginCache] 预加载资源失败:', error);
  }
}

/**
 * 获取缓存统计信息
 * @returns {Object} - 缓存统计信息
 */
export function getCacheStats() {
  try {
    const cached = getLoginCache();
    const timestamp = sessionStorage.getItem(CACHE_KEY.CACHE_TIMESTAMP);
    
    return {
      hasCached: !!cached,
      cacheSize: cached ? JSON.stringify(cached).length : 0,
      cacheAge: timestamp ? Date.now() - parseInt(timestamp, 10) : 0,
      isValid: isValidCache(),
      cachedFields: cached ? Object.keys(cached) : []
    };
  } catch (error) {
    console.error('[LoginCache] 获取统计信息失败:', error);
    return null;
  }
}

export default {
  initLoginCache,
  cacheLoginPageState,
  getLoginCache,
  isValidCache,
  clearLoginCache,
  clearSensitiveData,
  preloadLoginResources,
  getCacheStats
};
